import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { UserPlus, Loader2, Trash2, ShieldCheck, ShieldOff, KeyRound, X } from "lucide-react";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/users");
        if (!cancelled) setUsers(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load users");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  if (me?.role !== "owner") {
    return (
      <AdminLayout>
        <div className="hed-card p-10 text-center max-w-lg mx-auto">
          <div className="hed-overline text-[#9E2A2B]">Owner access only</div>
          <h2 className="font-serif text-2xl mt-3">Restricted page</h2>
          <p className="text-sm text-[#5C5C54] mt-2">
            Only owner-level accounts can manage staff users.
          </p>
        </div>
      </AdminLayout>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/users", form);
      setShowCreate(false);
      setForm({ email: "", name: "", password: "", role: "staff" });
      setTick((t) => t + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create user");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      setTick((t) => t + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email}? (min 8 chars)`);
    if (!pw) return;
    try {
      await api.patch(`/users/${u.id}`, { password: pw });
      alert("Password updated.");
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setTick((t) => t + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6 md:mb-8">
        <div className="min-w-0">
          <div className="hed-overline">Team</div>
          <h1 className="font-serif text-3xl sm:text-4xl mt-2 tracking-tight">Users & access</h1>
          <p className="text-sm text-[#5C5C54] mt-2 max-w-xl">
            Add staff who can upload and edit products. Owners can do everything; staff cannot delete products or manage users.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="add-user-button"
          className="inline-flex items-center gap-2 bg-[#B5953B] hover:bg-[#9A7E32] text-white px-4 py-2.5 md:px-5 md:py-3 rounded-sm font-semibold transition-colors text-sm"
        >
          <UserPlus size={16} strokeWidth={2} /> Add user
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#5C5C54]">Loading...</div>
      ) : (
        <div className="hed-card overflow-x-auto">
          <table className="w-full min-w-[520px]">
            <thead className="bg-[#FAFAF8] border-b border-[#E8E6DF]">
              <tr>
                <th className="text-left hed-overline px-5 py-3" style={{ fontSize: 10 }}>User</th>
                <th className="text-left hed-overline px-5 py-3" style={{ fontSize: 10 }}>Role</th>
                <th className="text-left hed-overline px-5 py-3" style={{ fontSize: 10 }}>Status</th>
                <th className="text-right hed-overline px-5 py-3" style={{ fontSize: 10 }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E6DF]">
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3">
                    <div className="text-sm font-semibold">{u.name || "—"}</div>
                    <div className="text-xs text-[#5C5C54]">{u.email}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-[10px] font-semibold tracking-wider uppercase px-2 py-1 rounded-sm ${
                        u.role === "owner"
                          ? "bg-[#B5953B]/15 text-[#B5953B]"
                          : "bg-[#F2F0EA] text-[#5E6033]"
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-[10px] font-semibold uppercase ${
                        u.active === false ? "text-[#9E2A2B]" : "text-[#3A5A40]"
                      }`}
                    >
                      {u.active === false ? "Disabled" : "Active"}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        title="Reset password"
                        onClick={() => resetPassword(u)}
                        className="p-2 hover:bg-[#F2F0EA] rounded-sm"
                        data-testid={`user-reset-${u.id}`}
                      >
                        <KeyRound size={14} strokeWidth={2} />
                      </button>
                      <button
                        title={u.active === false ? "Enable" : "Disable"}
                        onClick={() => toggleActive(u)}
                        className="p-2 hover:bg-[#F2F0EA] rounded-sm"
                        data-testid={`user-toggle-${u.id}`}
                      >
                        {u.active === false ? (
                          <ShieldCheck size={14} strokeWidth={2} className="text-[#3A5A40]" />
                        ) : (
                          <ShieldOff size={14} strokeWidth={2} />
                        )}
                      </button>
                      <button
                        title="Delete user"
                        onClick={() => del(u)}
                        className="p-2 hover:bg-[#9E2A2B]/10 hover:text-[#9E2A2B] rounded-sm"
                        data-testid={`user-delete-${u.id}`}
                      >
                        <Trash2 size={14} strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-sm text-[#5C5C54]">
                    No users yet. Click &ldquo;Add user&rdquo; to invite your first staff.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create user modal */}
      {showCreate && (
        <div
          className="fixed inset-0 z-50 bg-[#1A1A18]/40 flex items-center justify-center p-4"
          onClick={() => setShowCreate(false)}
        >
          <div
            className="bg-white border border-[#E8E6DF] rounded-sm w-full max-w-md p-6 hed-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="hed-overline">Invite</div>
                <h3 className="font-serif text-2xl mt-1">Add a new user</h3>
              </div>
              <button onClick={() => setShowCreate(false)} className="text-[#5C5C54] hover:text-[#1A1A18]">
                <X size={18} strokeWidth={2} />
              </button>
            </div>
            <form onSubmit={create} className="space-y-4 mt-4">
              <Field label="Full name">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="new-user-name"
                  className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-2 text-sm"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="new-user-email"
                  className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-2 text-sm"
                />
              </Field>
              <Field label="Temporary password (share with them; they can change later)">
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={8}
                  data-testid="new-user-password"
                  className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-2 text-sm"
                />
              </Field>
              <Field label="Role">
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="new-user-role"
                  className="w-full bg-white border border-[#E8E6DF] rounded-sm px-2 py-2 text-sm"
                >
                  <option value="staff">Staff — upload & edit products</option>
                  <option value="owner">Owner — full control</option>
                </select>
              </Field>
              {error && (
                <div className="text-xs text-[#9E2A2B] border-l-2 border-[#9E2A2B] pl-3 py-1">{error}</div>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="create-user-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A18] hover:bg-[#2a2a26] disabled:opacity-60 text-white px-5 py-2.5 rounded-sm text-sm font-semibold transition-colors"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} strokeWidth={2} />}
                Create user
              </button>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="hed-overline block mb-1" style={{ fontSize: 10 }}>{label}</label>
      {children}
    </div>
  );
}
